/**
 * Netx.View is a subclass of Backbone.View, and is the superclass of all other views in Netx.
 * It adds a number of features to Views: asyncronous loading of templates,
 * a pager, preRender() and postRender() hooks, a standard for defining template arguments,
 * a modular system for linking clicks to events (called "Actions"), child view nesting,
 * standard show() and hide() methods, and more.
 *
 * @class
 */

import Backbone from 'backbone';

const BBView = Backbone.View;
// Make a View we can extend from in other classes
class ES6View {
	/** @override */
	constructor(opts) {
		BBView.apply(this, arguments);
	}
}
_.extend(ES6View.prototype, BBView.prototype);
Backbone.ES6View = ES6View;

import Plugins from './netx-plugins';

import PagerModel from '@netx/core-app/models/pager';

import ActionsIconInlineTemplate from '@netx/core-app/templates/menuView-actionIconsInline.html';
import AssetDetailActionsTemplate from '@netx/core-app/templates/menuView-assetDetailActions.html';
import CartBarActionsTemplate from '@netx/core-app/templates/menuView-cartBarActions.html';
import ContextTemplate from '@netx/core-app/templates/menuView-context.html';
import HeaderActionsTemplate from '@netx/core-app/templates/menuView-headerActions.html';
import ListHeaderActionsTemplate from '@netx/core-app/templates/menuView-listHeaderActions.html';
import PodActionsTemplate from '@netx/core-app/templates/menuView-podActions.html';
import {simulateClickBody} from '@netx/core-next/utils/browser';
import {getEnabledActions} from '@netx/core-permissions/properties/actions';
import {appRouter} from '@netx/core-next/instance/router';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import {
	RegisterLegacyAction,
	FindLegacyAction,
} from '@netx/core/legacyActionManager';

// currently these are the only menu views that can be used
// but themes can override the templates
const MenuTemplates = {
	actionIconsInline: ActionsIconInlineTemplate,
	assetDetailActions: AssetDetailActionsTemplate,
	cartBarActions: CartBarActionsTemplate, // portal only
	context: ContextTemplate,
	headerActions: HeaderActionsTemplate,
	listHeaderActions: ListHeaderActionsTemplate,
	podActions: PodActionsTemplate, // theme only(?)
};

class View extends ES6View {
	/** @type {boolean} */
	get isView() {
		return this.constructor.__isView__;
	}

	/**
	 * Actions
	 * Netx Actions are an extensible framework for modules to register their functionality (event names).
	 * Actions can be added to views by adding 'data-action' attributes to HTML elements in view templates.
	 * The global Netx.Menus object composes groups of Netx.Action objects into Netx.Menu objects; these
	 * can be added to templates by adding the 'data-action-context' attribute to elements. ('context' is a deprecated name;
	 * we now prefer 'menu'.)  These are normally rendered as <ul> elements, but alternate templates can be
	 * specified via the 'data-template' attribute.
	 * Views automaticall convert those elements to buttons, menus and other UI elements, which
	 * then trigger the specified events.
	 *
	 * Actions can define Enabler functions and/or a UserLevel to specify when an Action can be fired.
	 * Actions beyond the user's level are hidden with $.hide(). Actions disabled by a function are
	 * disabled with $.disable() and given a class -- usually 'disabled', but that
	 * classname can be overridden by setting action.disabledClass
	 *
	 * By default, an action triggers its event with two arguments: the view, and the event object that triggered the action.
	 * However, an action can define a wrapper (action.wrapper), a function that takes those arguments and returns
	 * an array containing another set of arguments to trigger the event with.  This makes it easier for an action
	 * to target a general-purpose event handler that may already exist.
	 * A view can override the wrapper for any of its actions, by setting this.actionWrappers[eventName] to a similar function.
	 *
	 * An action that wants to trigger a route instead of an event can specify a 'route' property;
	 * this can be either a string or a function; the function is passed the action arguments and expected
	 * to return a string.
	 *
	 */

	/**
	 * Child View management
	 *
	 * this.childView defines a mechanism to let a collection View specify a different child view for its models.
	 * Backbone operations on the collection (add/remove/set/reset) will be automatically reflected in the view.
	 * When all child views are rendered, the event 'addedChildViews' is triggered -- on the parent view, not on the dispatcher.
	 *
	 * To use child views, specify a childView object similar to this one:
	 * @memberof View
	 * @member childView {object}
	 * @todo update example for view collections
	 * @example
	 * childView: {
	 *     // The Netx.View subclass of the child view (or defaults to Netx.View)
	 *     viewClass: 'SomeChildView',
	 *     // jQuery selector for the place in the parent view where the children will be inserted.
	 *     // NOTE: this feature will be deprecated.  Instead, label the desired childview element
	 *     // in the template with the className '.nx-childviews'
	 *     target: '#someplace',
	 *     // Can be a number to set the amount of items to insert at a time, before giving the browser a second to catch its breath,
	 *     // or an object that has more details about how to break up the adding of childViews
	 *     breakItUp: {
	 *         // (optional) The amount of items to add in pass
	 *         amount: 50,
	 *         // (optional) The amount of time to delay, in milliseconds, before making the next pass
	 *         delay: 0,
	 *     },
	 *     // operate on a different set than this.collection
	 *     collection: new Netx.Collection([...]),
	 *     // if true, add attribute draggable="true"
	 *     draggable: false,
	 *     // (optional) same thing for href (use on A els)
	 *     href: function( model ){ return '#foo/' + model.id },
	 *     // (optional) function to generate unique id per child from model
	 *     id: function( model ){ return 'foo-' + model.id },
	 *     // (optional) Set max childViews to show
	 *     maxItems: 5,
	 *     // if true, re-render the superview on every add. (Use with caution)
	 *     reset: true,
	 *     // Options passed in the options field of the child view's constructor
	 *     options: {
	 *         tagName: 'div',
	 *         className: 'et etcetera'
	 *     }
	 * }
	 */
	// get childView() {
	// 	return {};
	// }

	/**
	 * Events:
	 * Netx.View both triggers and listens for 'contextmenu' and 'showcontextmenu' events.  These are the only default handlers in the events object.
	 *
	 * Context Menus:
	 *
	 * Child views can use these events to display a context menu, defined by an Netx.Menu that is rendered in the template of a parent view.
	 * For parent views with lots of child views (such as asset lists) this avoids duplicating the HTML block of the menu for each child view, yielding better performance.
	 *
	 * To use context menus on child views, the childView definition should specify a Netx.Menu in a 'data-contextmenu-target' option in its definition, like so:
	 *
	 * @example
	 * // in the parent view's class definition:
	 *
	 * childView: {
	 *	options: {
	 *		attributes: {
	 *			'data-contextmenu-target': 'TheNameOfANetxMenu'
	 *		}
	 *	},
	 * },
	 *
	 * // in the parent view's template:
	 *
	 * <ul class="dropdown-menu" role="menu" data-action-context="TheNameOfANetxMenu"</ul>
	 * @object
	 * @see {@link Netx.View#findContextMenu|findContextMenu}
	 */

	/**
	 * Loading Classes:
	 *
	 * By default, Netx.View adds classes to every view's containing element
	 * to reflect the state of their models and/or collections:
	 *
	 * The HTML5 class 'loading' is added to every view's containing element
	 * while the view's model is fetched -- or its collection, if it has no model.
	 *
	 * If a view's collection is a subclass of Netx.Collections.Assets and is fetching a page chunk,
	 * it gets the class 'loading-chunk' instead of 'loading'.  (Chunk loading
	 * is used to implement infinite scroll, among other effects.)
	 *
	 * If a view's collection finishes fetching and has no models, the view
	 * gets the class 'no-assets'.
	 *
	 * The class 'not-loaded' is added to the view's containing element at init,
	 * and removed permanently at the first fetch.
	 *
	 * CSS styles can use these classes to give users visual feedback on the status of transactions.
	 *
	 * this.loadingMethods is an array of strings specifying the backbone.RPC methods that trigger this behavior;
	 * by default this is the single string "read", but child views can modify the array
	 * to get the same effect on other methods, or delete it to disable this behavior entirely.
	 *
	 * When calling an RPC method, if the property 'loadingClasses' is set false in
	 * the optional Backbone options object, the behavior will be supressed for
	 * the duration of that RPC method call.
	 *
	 * To disable or modify this behavior, add the array 'loadingClassObjects' to the view,
	 * either in its definition or as an option when instantiated.
	 * Then, only the objects in that array will be listened to for fetch events.
	 * (An empty array will disable loading classes completely.)
	 */

	/**
	 * @override
	 * @param {object} options View options
	 */
	constructor(opts = {}) {
		// Sanity check: have we been handed a null or bogus 'el' option?
		// did we get a valid options.el?
		// does that element not exist?
		if (_.isString(opts.el) && !document.querySelector(opts.el)) {
			console.warn(
				`Could not attach view to DOM: DOM element ${opts.el} does not exist.`,
			);
			// ...so the backbone.view initializer will create a new one.
			delete opts.el;
		}

		opts.events = {
			// A handler for the contextmenu event
			contextmenu: 'findContextMenu',
			'click .nx-cancel:first': 'cancel',
			'click [data-nx-control="close"]:first': 'cancel',
			'click .nx-done:first': 'hide',
			// A handler for popout views:
			'click a[data-nx-popout]': 'togglePopout',
			...opts.events,
		};
		super(opts);

		this.args = opts.args || [];
		this.headerTitle = opts.headerTitle || '';
		this.routeMatch = opts.routeMatch;
		this.viewSpec = opts.viewSpec;
		this.template = opts.template;
		this.loadingClassObjects = opts.loadingClassObjects;
		this.loadingClassRemoveDelay = opts.loadingClassRemoveDelay || 0;
		this.listenToCollection = opts.listenToCollection;
		this.modalOptions = opts.modalOptions || {};
		this.properties = opts.properties || {};
		this.removeOnHide = opts.removeOnHide !== false;
		this.removeOnSimpleHide = opts.removeOnSimpleHide !== false;
		this.requireSync = opts.requireSync === true;
		this.showLoadingOnce = opts.showLoadingOnce === true;
		this._loadedOnce = false;

		//opts.childView && ChildViews(this, opts.childView);

		// Debounced/throttled methods
		// Debounced render for listeners
		// We are doing this in a lot of views - until we find something better (since we cannot debounce the actual render method):
		this.debouncedRender = _.debounce(this.render, 100);

		this.configureSubmenus = _.debounce(this.configureSubmenus, 100);

		// Keep track of how many views we maintain:
		Netx.viewStats = Netx.viewStats || {};
		Netx.viewStats[this.constructor.__name__] =
			(Netx.viewStats[this.constructor.__name__] || 0) + 1;

		/**
		 * this.subViews is an array where any view can manually place a reference to a subview.  The promise returned by
		 * have finished rendering.  Calls to this.remove() will recursively call all those views.
		 */
		this.subViews = {};

		/**
		 * Template Variables.
		 *
		 * By default, if this.model is defined in a template, all of that model's attributes will be available as template variables.
		 * Otherwise, if this.collection is defined, all properties of the collection object will be made into template variables.
		 *
		 * Furthermore, if the view does not override this.doTemplate(), any/every other key/value added to the object this.templateVars
		 * will be translated into additional template variables for the view.  This is an easy way to add extra template variables
		 * and is good enough in most situations.
		 *
		 * if this.templateVars is defined as a function, the function will be expected to return an object, which will then be converted to variables.
		 */
		this.templateVars = this.templateVars || {};

		// Plugin manager
		if (!this.Plugins || !(this.Plugins instanceof Plugins)) {
			this.Plugins = new Plugins(this);
		}

		// Some plugins that get used a lot
		this.Plugins.Register({
			resizeListener: {
				name: 'resize',
				initializeMethod: function (pluginObj, $pluginEl, initOpts, cb) {
					$pluginEl.resize(_.bind(this.resize, this));
					// This is an odd little plugin - but helpful
					cb({
						el: $pluginEl,
					});
				},
				destroyMethod: function (instance) {
					// Again, an odd (but helpful) plugin
					instance.el[0].__resizeTriggers__ && instance.el.removeResize(null);
				},
			},
		});

		/**
		 * @type {string}
		 */
		this.i18nCountStr =
			opts.i18nCountStr !== false ? opts.i18nCountStr || 'i18n.nItems' : false;
		/** @type {boolean} */
		this.isRouted = opts.isRouted === true;
		/**
		 * Set a delay on the removal of loading classes
		 * Especially useful in views where we debounce renders on sync
		 * @type {number}
		 * @default
		 */
		this.loadingClassRemoveDelay = opts.loadingClassRemoveDelay || 0;

		/**
		 * RPC methods to listen for loading on
		 * @type {array}
		 */
		this.loadingMethods = opts.loadingMethods || ['read'];

		/**
		 * Normally, a route change clears all modal windows.
		 * If the stickyModal flag is set on a modal view, the view will remain
		 * on screen through route changes.
		 * This will automatically be set to true if a view has `modalOptions.blocking` set to `true`
		 * @type {boolean}
		 * @see {@link Netx.View#isMyRoute|isMyRoute}
		 */
		this.stickyModal = opts.stickyModal === true;

		/**
		 * What to extend data from when we run doTemplate
		 * If left unset it defaults to standard behavior - we check for `this.model`, `this.collection`, and lastly, fallback to `this`
		 * @type {object}
		 * @default
		 */
		// this.templateDataSource = opts.templateDataSource || {};

		/**
		 * A Deferred object that resolves when this view finishes rendering.
		 * This is a Deferred object, and is resolved after the first render.
		 * @deprecated 2016-07-01 - Left for legacy as no longer are we deferred thanks to pre-compiling our templates
		 * @type {object}
		 */
		// TODO: Is this really deprecated yet?  It's still used in a lot of code,
		// and it does have the effect of postponing thing until a view's first render....
		this.rendered = new $.Deferred();

		////////////////////////////////////////
		// SHOWN/HIDDEN PROMISES
		////////////////////////////////////////
		// Initial shown/hidden promises
		this._hiddenDeferred = new $.Deferred();
		this.whenHidden = this._hiddenDeferred.promise();
		this._shownDeferred = new $.Deferred();
		this.whenShown = this._shownDeferred.promise();
		////////////////////////////////////////
		// !SHOWN/HIDDEN PROMISES
		////////////////////////////////////////

		// Bind
		this.bindCollectionEvents().bindModelEvents();

		// Hey, listen!
		this
			// listen for model & collection changes
			.listenTo(this, 'setModel', this.setModel)
			.listenTo(this, 'setCollection', this.setCollection)
			// Listen for showing/shown hiding/hidden events
			.listenTo(this, 'hiding', this.onHiding)
			.listenTo(this, 'hidden', this.afterHidden)
			.listenTo(this, 'showing', this.onShowing)
			.listenTo(this, 'shown', this.afterShown)
			// On hidden create new promise for shown
			.listenTo(this, 'hiddenByProxy', () => {
				if (!this.removing && !this.hiding && !this.showing) {
					// If we were visible at the time
					if (this.isShown) {
						this.hiddenByProxy = true;
						this.afterHidden(this);
					}
				}
				this.shownByProxy = false;
			})
			// On shown create new promise for hidden
			.listenTo(this, 'shownByProxy', () => {
				if (!this.removing && !this.hiding && !this.showing) {
					if (this.isShown || this.hiddenByProxy) {
						this.shownByProxy = true;
						this.afterShown(this);
					}
				}
				this.hiddenByProxy = false;
			});

		// Set up any additonal functionality
		const __initializers__ = this.constructor.__initializers__ || [];
		__initializers__.forEach((func) => func(this, opts));

		// Add loading classes
		// Adding in render gets into to timing issues with lsiteners and repsonses
		// should a request be in progress
		this.addLoadingClasses_internal();
	}

	/**
	 * Adds loading classes to the the view container
	 * @method
	 * @private
	 */
	addLoadingClasses_internal() {
		// NOTE: no way to check options here for loadingClasses === false.

		if (!this.loadingMethods || !this.loadingMethods.length) {
			return;
		}

		let showNotLoaded = false;
		let thingSyncing;

		const loadingClassObjects = this.loadingClassObjects || [];

		// First loop over any defined loading class objects and check if they are currently syncing
		if (loadingClassObjects.length) {
			loadingClassObjects.forEach((item) => {
				if (_.isString(item) && this[item] && this[item].isSyncing) {
					thingSyncing = this[item];
				} else if (_.isObject(item) && item.isSyncing) {
					thingSyncing = item;
				}
			});
		}

		// TODO: this.model.isSyncing could contain complete sync info instead of a boolean ....
		// If nothing already syncing and view is not tied to a collection and it has a model that is syncing
		if (
			!thingSyncing &&
			!this.collection &&
			this.model &&
			this.model.isSyncing
		) {
			thingSyncing = this.model;
		}
		// Have we found anything syncing yet, if not check this.collection
		else if (!thingSyncing && this.collection && this.collection.isSyncing) {
			thingSyncing = this.collection;
		}
		// Either the collection has never synced or the model is new - nothing loaded
		else if (
			(!this.collection && this.model && this.model.isNew()) ||
			(this.collection && this.collection.syncedOnce !== true)
		) {
			showNotLoaded = true;
		}

		const thingListener = (thingRequesting, xhr, opts) => {
			// Prevent models from triggering request events on collections, unless a model is specified in loadingClassObjects
			if (
				(thingRequesting.isModel && loadingClassObjects.includes('model')) ||
				(thingRequesting.isCollection &&
					loadingClassObjects.includes('collection'))
			) {
				this.showLoading(thingRequesting, xhr, opts);
			}
		};

		// This function adds to some Backbone object the listeners for adding & removing the loading classes
		const addListeners = (thing) => {
			if (!thing || !thing._listenId) return;

			_.each(this.loadingMethods, (arg) => {
				this.stopListening(thing, `request:${arg}`, thingListener).listenTo(
					thing,
					`request:${arg}`,
					thingListener,
				);
			});

			this.stopListening(thing, 'sync', this.showLoaded).listenTo(
				thing,
				'sync',
				this.showLoaded,
			);
		};

		// We have a syncing model or collection
		if (thingSyncing) {
			// if addLoadingClasses is called while the model/collection is fetching, reflect that immediately.
			this.showLoading(thingSyncing);
			addListeners(thingSyncing);
		}
		// Nothing loaded
		else if (showNotLoaded) {
			this.showNotLoaded();
		}
		// We must already have all we need to be considered "loaded"
		else {
			// TODO: need to include the loadingclassobjects some how
			// we were getting false positives before on no-assets because showLoaded needs
			// a `thing` to reference to make sure we are not adding classes based on some rogue
			// thing that may have been syncing at one time - really we need to sit down and
			// discusss this as there are a lot of problems with our current approach
			// Everything appeared to work so brilliantly when we had a template lag >.<
			this.showLoaded(this.collection || this.model, null, {noDelay: true});
		}

		// What should we listen on?
		// if this.loadingClassObjects is set, use the objects it points to.
		loadingClassObjects.forEach((item) => {
			let thingToListenTo;
			if (_.isString(item)) {
				if (!_.isUndefined(this[item])) {
					thingToListenTo = this[item];
				}
			} else {
				thingToListenTo = item;
			}
			if (
				!thingSyncing ||
				thingSyncing._listenId !== thingToListenTo._listenId
			) {
				addListeners(thingToListenTo);
			}
		});

		return this;
	}

	/**
	 * Add a pager to this collection view
	 * @param {Netx.Collection} c Netx.Collection instance -- defaults to this.collection
	 */
	addPagerView(c) {
		c = c || this.collection;
		if (!c) {
			console.warn('cant add pager to view without a collection!');
			return;
		}

		const pagers = this.$('[data-nx-pager]');
		if (pagers.length > 1) {
			// ALERT! Sensitivity training error! Report to HR for reprogramming!
			//console.error( 'To many pager Daniel-san! Only one `data-nx-pager` element per template.' );
			console.warn('Only one `data-nx-pager` element allowed per template.');
		} else if (pagers.length === 0) {
			console.warn('Cant find a `data-nx-pager` element in template.');
		} else {
			if (this.pagerView) {
				this.removeSubView(this.pagerView, true);
			}
			import(
				/* webpackChunkName: "view-pagerView" */ '@netx/core-app/views/pagerView'
			).then((PagerView) => {
				const $pager = pagers[0];
				this.pagerView = new PagerView.default({
					collection: c,
					model: c.pager || new PagerModel(),
				});
				this.addSubView(this.pagerView);
				this.pagerView.setElement($pager).render();
			});
		}
	}

	/**
	 * Add any old view as a 'subview' to this view.  It will be stored in this.subViews{} using view.cid as the key.
	 * This view will manage the subview's life cycle, destroying it when this view is destroyed,
	 * and this view's render promise will not resolve until all subviews are also done rendering.
	 * All other management (like triggering render) must be handled by the parent view.
	 *
	 * Unlike this.childViews{}, this.subViews{} is totally seperate from the child view system for collections & their models.
	 * Views in this.subViews{} are not destroyed in the render cycle.
	 * @method
	 * @param {Netx.View} v
	 * @returns {Netx.View} view Sub view
	 */
	addSubView(v) {
		this.subViews[v.cid] = v;
		return v;
	}

	/**
	 * Everything has happend in the core hiding of this view
	 * Executes just before `whenHidden` is resolved
	 * @method
	 * @listens Netx.View#hidden
	 */
	afterHidden(view) {
		if (view !== this || this.removing) {
			return;
		}
		// Set the appropriate hidden stuff
		this.hiding = false;
		this.isShown = false;

		this.el.removeAttribute('data-nx-hiding');
		this.el.removeAttribute('data-nx-showing');
		// this.$el.removeClass('showing hiding');

		if (this.viewSpec) {
			this.$el.removeClass('active');
		}

		if (this.whenHidden.state() === 'pending') {
			this._hiddenDeferred.resolveWith(this);
		}
		if (this.whenShown.state() === 'pending') {
			this._shownDeferred.reject();
		}

		_.invoke(this.subViews, 'trigger', 'hiddenByProxy', this);

		_.defer(() => {
			// New shown promise
			this._shownDeferred = new $.Deferred();
			this.whenShown = this._shownDeferred.promise();
		});
	}

	/**
	 * Everything has happend in the core showing of this view
	 * Executes just before `whenShown` is resolved
	 * @method
	 * @listens Netx.View#shown
	 */
	afterShown(view) {
		if (view !== this || this.removing) {
			return;
		}

		// Set the appropriate shown stuff
		this.isShown = true;
		this.showing = false;

		this.el.removeAttribute('data-nx-hiding');
		this.el.removeAttribute('data-nx-showing');
		this.$el.removeClass('showing hiding');

		if (this.viewSpec) {
			this.$el.addClass('active');
		}

		if (this.whenShown.state() === 'pending') {
			this._shownDeferred.resolveWith(this);
		}
		if (this.whenHidden.state() === 'pending') {
			this._hiddenDeferred.reject();
		}

		_.invoke(this.subViews, 'trigger', 'shownByProxy', this);

		_.defer(() => {
			// New hidden promise
			this._hiddenDeferred = new $.Deferred();
			this.whenHidden = this._hiddenDeferred.promise();
		});
	}

	/**
	 * This is where the any animation should happen
	 * When the callback is executed the app considers this view as good as hidden
	 * @method
	 * @param {function} cb Callback
	 * @see Netx.View#hide
	 */
	beforeHidden(cb) {
		this.el.style.display = 'none';
		cb.apply(this);
	}

	/**
	 * This is where the any animation should happen
	 * When the callback is executed the app considers this view as good as shown
	 * @method
	 * @param {function} cb Callback
	 * @see Netx.View#show
	 */
	beforeShown(cb) {
		this.el.style.display = '';
		cb.apply(this);
	}

	/**
	 * @method
	 */
	bindCollectionEvents() {
		const collection = this.listenToCollection || this.collection;
		if (!collection) return this;

		this.trigger('bindCollectionEventsHook', this);

		return this.listenTo(collection, 'reset add remove', () => {
			this.collectionUpdated(collection);
		})
			.listenTo(collection, 'sync', (thing) => {
				if (thing !== collection) return;
				this.collectionUpdated(collection);
			})
			.listenTo(collection, 'updatedTotal', this.collectionUpdated);
	}

	/**
	 * @method
	 */
	bindEvents() {
		this.unbindEvents();

		// DAM-13638
		if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
			const menuName = this.$el.data('contextmenu-target');
			if (!menuName) return this;

			if (!this.hammer) {
				this.hammer = new Hammer(this.el);
			}

			this.hammer.on('press', (e) => {
				// e.preventDefault();
				this.findContextMenu(e.srcEvent);
			});
		}

		return this;
	}

	/**
	 * @method
	 */
	bindModelEvents() {
		if (!this.model) return this;

		return this.trigger('bindModelEventsHook', this);
	}

	/**
	 * A helper method for dealing with large arrays or objects of data
	 * We can set a slight delay to allow the update of progress (etc) and not just have a locked up browser
	 * @method
	 * @param {object}   options          Options (like size, amount, start, delay)
	 * @param {function} processCallback  Is called on each iteration and passes the current index
	 * @param {function} completeCallback Is called when all iterations are complete - return data here
	 */
	breakItUp(options, processCallback, completeCallback) {
		// Helpers
		const d = new $.Deferred();

		// Vars
		var start = _.isUndefined(options.start) ? 0 : options.start,
			idx = 0,
			amount = options.amount || 10,
			increment = options.increment || 1,
			size = options.size,
			delay = options.delay || 2,
			self = this;

		// I am trying this out so that we might be able to give some progress indication in the event that
		// we are trying to edit a whole lot of cells (so the screen isn't just locked up)
		var brokenUp = function (start, amt) {
			// No delay on first call
			idx === 0 ? process(start, amt) : setTimeout(process, delay, start, amt);
		};
		// So we are not creating a new anonymouse function each chunk we iterate over (delays and all)
		var process = function (start, amt) {
			// Vars
			var end, processed;

			// Keep in bounds
			if (start < 0) start = 0;
			end = start + amount;
			if (end > size) end = size;

			var c, i;
			// Process
			for (i = start, c = end; i < c; i += increment) {
				// Return false in your process callback to end the loop and just fire the complete callback
				processed = processCallback.call(self, i);
				idx++;
				if (processed === false) break;
			}

			// Next
			if (processed !== false && i <= size - 1) {
				brokenUp(i, amount);
			}
			// Finished
			else {
				d.resolveWith(self, [
					_.isFunction(completeCallback) ? completeCallback.call(self) : null,
				]);
			}
		};
		// Start
		brokenUp(start, amount);

		return d.promise();
	}

	/**
	 * @method
	 * @param {event} e jQueryEvent
	 */
	cancel(e) {
		if (e && e.preventDefault) {
			e.preventDefault();
			e.stopPropagation();
		}

		this.trigger('cancelled', this);
		this.hide();

		if (this.eventStr) {
			dispatcher.trigger(
				`${this.eventStr}:cancelled`,
				this.model || this.collection,
			);
		}
	}

	/**
	 * Differntiate between having `no-assets` and having `no-matches`
	 * @method
	 */
	checkHasResults() {
		const collection = this.listenToCollection || this.collection;
		if (
			!collection ||
			collection.syncing ||
			(this.requireSync && !collection.syncedOnce)
		)
			return;

		const el = this.el;

		el.removeAttribute('data-nx-no-matches'); //removeClass('no-matches');

		if (collection.isFiltered()) {
			el.removeAttribute('data-nx-no-assets');
			el.removeAttribute('data-nx-no-results');
			collection.length === 0 && el.setAttribute('data-nx-no-matches', true);
			return;
		}

		const hasResults = _.isFunction(this.hasAssets)
			? this.hasAssets()
			: collection.getTotal();
		if (hasResults) {
			el.removeAttribute('data-nx-no-assets');
			el.removeAttribute('data-nx-no-results');
		} else {
			el.setAttribute('data-nx-no-assets', true);
			el.setAttribute('data-nx-no-results', true);
		}
	}

	/**
	 * Often, a model wants to remove itself from the DOM when its model is removed from a collection.
	 * checkRemove() is a handler for Backbone "remove" events on models, which pass a model, a collection and options.
	 * It calls .remove() only if the event's collection argument matches this.collection .
	 * @method
	 * @param {Backbone.Model} model
	 * @param {Backbone.Collection) collection
	 * @param {Object} opts
	 */
	checkRemove(model, collection, opts) {
		if (collection == this.collection) {
			return this.remove();
		}
	}

	/** @method */
	cleanUpActionEvents() {
		_.forEach(this.actionEvents, (event, action) => {
			_.each(event.els, function (el) {
				el.removeEventListener('click', event.func);
				el.removeEventListener('keydown', event.func);
				$(el).off('tapToClick', event.func);
			});
		});
		this.actionEvents = {};
	}

	/**
	 * @method
	 * @param {module:core/lib/netx-collection} collection Collection
	 */
	collectionUpdated(collection) {
		const _collection = this.listenToCollection || this.collection;
		if (collection !== _collection) return;

		this.checkHasResults();
		this.updateCount();
	}

	/**
	 * configureActions: for a set of actions, re-test all Enablers and userlevel tests, and enable/disable the Actions as needed.
	 * This happens automatically with rendering, but can be useful to trigger manually.
	 * @param {array} actionList the set of actions
	 * @param {Netx.View} [otherView] operate on a view other than this one.
	 */
	configureActions(actionList, otherView) {
		if (!actionList.length) return;

		const view = otherView != null ? otherView : this;
		const actionOpts =
			otherView && otherView !== this ? {targetView: otherView} : undefined;

		const enabledActions = getEnabledActions(actionList, view);
		const disabledActions = actionList.filter(
			(action) => !enabledActions.includes(action),
		);

		enabledActions.forEach((action) => {
			if (this.$actionEls) {
				const $target = this.$actionEls.filter(
					`[data-action="${action.event}"]`,
				);

				$target.attr('title', action.label);
				this.enableAction(action, actionOpts);
			}
		});

		disabledActions.forEach((action) => {
			if (this.$actionEls) {
				const $target = this.$actionEls.filter(
					`[data-action="${action.event}"]`,
				);

				$target.attr('title', action.label);
				this.disableAction(action, actionOpts);
			}
		});

		const menuActions = this.menuActions;
		// Reconfigure any menus that need it:
		let menusToConfigure = [];
		actionList.forEach((action) => {
			for (const menuName in menuActions) {
				if (menuActions.hasOwnProperty(menuName)) {
					menuActions[menuName].includes(action) &&
						!menusToConfigure.includes(menuName) &&
						menusToConfigure.push(menuName);
				}
			}
		});
		// configure each menu once, not once for each menu item
		menusToConfigure.forEach((menuName) => {
			this.configureMenu(menuName);
		});

		// Super gross
		// A view containing the menu calls on `this.configureMenu` with the menu it contains
		// but the menu view is responsible for checking its actions and enabling/disabling them
		// this can happen after the parent view has already called `this.configureMenu`
		// There seems to be no reason why a parent view should be handling this since it is trying
		// to directly control the top level element that is them menu view's el - and it knows
		// everything that needs to be known about the actions within and itself
		// Rather than trying to tackle the right way now, holding off for our menu/actiomns refactor
		// which is greatly needed
		if (this.isMenuView && this.$el.hasClass('nx-action-context')) {
			this.configureMenuView(this);
		}

		// re-initialize backbone's listeners off this.events
		this.delegateEvents();

		// done.
		this.trigger('configuredActions', actionList);
	}

	/**
	 * Enable/disable a menu based on whether it contains any enabled actions.
	 * @method
	 * @param {module:app/views/menuView} menuView Menu view
	 * @param {Element} menuEl Menu element (if not specified will use menuView.$el)
	 */
	configureMenuView(menuView, $menuEl) {
		$menuEl = $menuEl || menuView.$el;
		const $menuToggle = $menuEl.siblings('[data-toggle="dropdown"]');

		// Check if we have any enabled actions
		let allActions = menuView.$('.nx-action');
		let disabledActions = menuView.$('.nx-action[disabled]');

		// TODO: maybe there's a bug here:
		// if a menu has a mix of actions and hrefs,
		// and one href is the only enabled item,
		// will this 2-step check work right?
		//
		// There should not be, this double checks seems pointless as all things
		// in a menu are actions and should have a `.nx-action` class -jse-

		// Maybe it is just href based and not data-action
		if (!allActions.length) {
			allActions = menuView.$('[href]');
			disabledActions = menuView.$('[href][disabled]');
		}

		// Configure the menu element: disable if all its actions are disabled.
		$menuEl.enableIf(disabledActions.length < allActions.length);
		// If the menu is a dropdown, also configure its toggle.
		$menuToggle.enableIf(disabledActions.length < allActions.length);

		menuView.configureSubmenus();
	}

	/**
	 * Configure menu by name
	 * @method
	 * @param {string} menuName Menu name
	 */
	configureMenu(menuName) {
		if (!this.menuViews) {
			return;
		} // no menu views yet (something is configuring before we've rendered.)

		var menuView = this.menuViews[menuName];
		var $menuEl = this.$(
			'.nx-action-context[data-action-context="' + menuName + '"]',
		);
		if (!menuView || !$menuEl.length) {
			// Let's not be so noisy about it
			console.warn("Can't find menu " + menuName);
			return;
		}

		this.configureMenuView(menuView, $menuEl);
	}

	/** @method */
	configureSyncActions() {
		this.configureActions(this.syncActions);
	}

	/** @method */
	configureUserActions() {
		this.configureActions(this.userActions);
	}

	/**
	 * If we have unsaved changes, confirm that we want to hide
	 * This method is only automatically called from a `multi-view` world when `hide` is triggered on a top level view (and up for removal)
	 * For this to fire under any other circumstance requires manually calling where needed
	 * @return {boolean}
	 * @fires module:app#app:confirmUnsaved
	 */
	confirmHide() {
		const d = new $.Deferred();
		let unsavedView =
			_.isFunction(this.unsavedChanges) && this.unsavedChanges();

		if (unsavedView) {
			dispatcher.trigger('app:confirmUnsaved', {
				confirmed: (...args) => {
					// Trigger event
					unsavedView.trigger('nx:unsavedChanges:confirmed');
					// Resolve with this view (may or may not be the actual unsaved view)
					d.resolveWith(this, [unsavedView].concat(...args));
				},
				cancelled: (...args) => {
					// Trigger event
					unsavedView.trigger('nx:unsavedChanges:cancelled');
					// Reject and pass the unsaved view
					d.rejectWith(this, [unsavedView].concat(...args));
					// Show the view in question
					// Basically, `this.unsavedChanges` returns a view that has unsaved changes - this could be a subView/chilView
					// If it does return a view other than itself, and this view is a multiView as well, show that view and the higher `multiView` will show this view on reject
					if (this !== unsavedView && this.isMultiView) {
						this.isTabView
							? this.showTab(unsavedView)
							: this.showView(unsavedView);
					}
				},
			});
		} else {
			d.resolveWith(this);
		}
		return d.promise();
	}

	/**
	 * disableAction: disables an action.  This happens automatically when actions have Enablers.
	 * @method
	 * @private
	 * @param {object} action An Action object, or an event string that identifes a registered Action object
	 * @param {boolean} [options.redelegate] Should delegateEvents() be called?
	 */
	disableAction(action, options) {
		if (_.isString(action)) {
			action = FindLegacyAction(action); // convert action name to action object
		}

		const $target = this.$actionEls.filter(`[data-action="${action.event}"]`);
		const disabledClass = _.isString(action.disabledClass)
			? action.disabledClass
			: 'disabled';

		$target.addClass(disabledClass).disable();

		// if this is the first action and it is disabled, this.actionEvents wont be defined
		this.actionEvents = this.actionEvents || {};

		const actionEvent = this.actionEvents[action.event];
		if (actionEvent) {
			_.each(actionEvent.els, function (el) {
				el.removeEventListener('click', actionEvent.func);
				$(el).off('tapToClick', actionEvent.func);
			});
			delete this.actionEvents[action.event];
		}

		this.trigger('disabledAction', action, options);
	}

	/**
	 * Disable the target element involved in the event
	 * @method
	 * @param {event} e Event
	 */
	disableTarget(e) {
		e && e.target && $(e.target).disable();
	}

	/**
	 * Netx.View templates are processed by Underscore's _.template() command, which can take various arguments.
	 * Sub-classes can override this.doTemplate() to specify what arguments are passed to that templating function.
	 *
	 * The default implementation is useful; it passes the entire collection (for collection views),
	 * or all the model attributes (for model views), or the entire view (for views without a model or collection).
	 * @see {@link Netx.View#render|render}
	 * @see {@link Netx.View#renderElement|renderElement}
	 * @method
	 * @return {object} this
	 */
	doTemplate() {
		if (!this.template) return '';
		// Additional template variables
		// This is a helper - you will always get the model or collection for free
		// adding things to this.templateVars will make it so you do not have to override this doTemplate method
		// If you have a doTemplate override in your view it will be up to you to recreate this functionality or not
		// The only reason I can see, going forward, to have a custom doTemplate method is if it is absolutely imperative
		// to not pass the model/collection/view (depending on what is there)
		let additionalVars = {};
		if (_.isFunction(this.templateVars)) {
			additionalVars = this.templateVars();
		} else if (_.isObject(this.templateVars)) {
			additionalVars = _.extend({}, this.templateVars);
		}

		if (this.model) {
			additionalVars.isNew = this.model.isNew();
		}

		let opts = {};
		// Defined templateDataSource
		if (_.isObject(this.templateDataSource)) {
			opts = _.extend({}, this.templateDataSource, additionalVars);
		}
		// Model view
		else if (this.model && this.model.attributes) {
			opts = _.extend({}, this.model.attributes, additionalVars);
		}
		// Collection view
		else if (this.collection) {
			opts = _.extend({}, this.collection.templateProps, additionalVars);
		}
		// Generic view
		// TODO: this needs to go away - if a view has not specified a data source, it gets no data in the template!
		else {
			opts = _.extend({}, this, additionalVars);
		}

		const headerTitle = this.headerTitle;
		if (headerTitle != null && opts.headerTitle == null) {
			opts.headerTitle = headerTitle;
		}
		return this.template.call(this, opts);
	}

	/**
	 * enableAction: enables an action.  This happens automatically when actions have Enablers.
	 * @method
	 * @private
	 * @param {} action An Action object, or an event string that identifes a registered Action object
	 * @param {boolean} [options.redelegate] Should delegateEvents() be called?
	 * @param {boolean} [options.targetView] Trigger the action on a view other than this one.
	 */
	enableAction(action, options) {
		if (_.isString(action)) {
			action = FindLegacyAction(action); // convert action name to action object
		}

		const $target = this.$actionEls.filter(`[data-action="${action.event}"]`);

		const disabledClass =
			typeof action.disabledClass === 'string'
				? action.disabledClass
				: 'disabled';

		$target.removeClass(disabledClass).enable();

		// Add a listener for the action's click events, via the standard Backbone.View events list.
		this.events = this.events || {};

		this.actionEvents = this.actionEvents || {};

		const actionEvent = this.actionEvents[action.event];

		if (actionEvent) {
			_.each(actionEvent.els, function (el) {
				el.removeEventListener('click', actionEvent.func);
				$(el).off('tapToClick', actionEvent.func);
			});
		}

		var clickEvent = (e) => {
			if (e) {
				// Be respectful
				if (
					e.defaultPrevented ||
					(e.originalEvent && e.originalEvent.defaultPrevented) ||
					// not mouse click, but not enter or space either
					(e.which !== 1 && e.which !== 13 && e.which !== 32)
				) {
					return;
				}
				// Actions are superseding events within them
				if (e.target.closest('.nx-ignore-action')) return;
			}

			// The click doesn't get up to the menu view
			if (this.isMenuView) {
				dispatcher.trigger('app:close:menus');
			}

			// With action & arguments, perform the appropriate action:
			this.performAction(action, [this, e]);
			// Our return value will control subsequent processing of this click event.
			// Set it by tag type:
			var tag = $(e.currentTarget).prop('tagName');
			tag = tag && tag.toLowerCase();
			if (tag == 'a') {
				e.preventDefault();
			}

			simulateClickBody();

			// Our return value will control subsequent processing of this click event.
			// We do not want to prevent default behavior for inputs and textareas. (return true)
			// For all other elements, tell jQuery we are done processing this click. (return false)
			// THIS IS SO GROSS:
			if (
				!(
					tag == 'input' ||
					tag == 'select' ||
					tag == 'textarea' ||
					e.currentTarget.closest('.mdc-drawer')
				)
			) {
				//e.stopPropagation();
				return false;
			}
		};

		if ($target.length) {
			_.each($target, function (el) {
				el.addEventListener('click', clickEvent);
				el.addEventListener('keydown', clickEvent);
				$(el).on('tapToClick', clickEvent);
			});
			this.actionEvents[action.event] = {els: $target, func: clickEvent};
		}

		this.trigger('enabledAction', action, options);
	}

	/**
	 * End progress simulation
	 * @method
	 */
	endProgressSimulation() {
		// Must have been simulating to end simulation
		if (!this.simulatingProgress) return;

		window.requestAnimationFrame(() => {
			this.$pseudoProgressBar.width('100%').attr('aria-valuenow', '100%');
		});

		// Simualtion over
		this.simulatingProgress = false;

		_.delay(() => {
			if (this.simulatingProgress) return;
			this.el.removeAttribute('data-nx-simulating-progress');
			this.$pseudoProgress.removeClass('active');
			this.$pseudoProgressBar.width('0%').attr('aria-valuenow', 0);
		}, this.loadingClassRemoveDelay || 300);
	}

	/** @method */
	escapeKeyListener() {
		if (!this.$el.is(':visible')) return;
		this.escape();
	}

	/**
	 * this.findContextMenu(): contextmenu event (right-click, etc) handler for context menus.
	 *
	 * A Netx.View may define the attribute 'data-contextmenu-target' on its element to specify that
	 * right clicks should show a context menu containing the Actions of a Netx.Menu
	 * The value of 'data-contextmenu-target' is the name of the menu.
	 *
	 * You should not need to call this function directly.
	 *
	 * @method
	 * @param {event} e Event
	 * @private
	 * @see {@link Netx.View#insertContextMenu|insertContextMenu}
	 */
	findContextMenu(e) {
		// If we have data-contextmenu-target set, we are a view trying to have a menu.
		const menuName = this.$el.data('contextmenu-target');
		if (!menuName) return true;

		simulateClickBody();

		// insert context menu
		this.insertContextMenu(menuName, e);
		// Prevent browser from getting this click
		return false;
	}

	/**
	 * Get this.templateVars
	 * @method
	 * @return {function|object} Template variables
	 */
	getTemplateVars() {
		return this.templateVars;
	}

	/**
	 * Hide the view
	 * @method
	 * @param {boolean} simple Simply a flip
	 * @fires Netx.View#hiding
	 * @fires Netx.View#hidden
	 * @see {@link Netx.View#show|show}
	 * @see {@link Netx.View#toggle|toggle}
	 */
	hide(simple, extra) {
		// used to be function (route, simple)
		if (extra) {
			// if we got two args
			console.warn(
				'hide() no longer takes a route argument.  Fix before v8.10',
			);
			simple = extra;
		}

		// Sometimes events get piped to this method - trying to find them all, but for any remaining
		if (simple && !_.isBoolean(simple)) {
			simple = false;
		}

		// This could change in the interim (while the beforeHidden method runs)
		// Say there was a simple hide - but while the view was hiding some conditions were met to call a hard hide - so set it and
		// delete it later - it should never be set outside of right here
		if (this._simpleHide !== false) {
			this._simpleHide = simple === true;
		}

		// Do nothing if we are in the midst of being removed
		// or are already preparing to hide
		if (this.removing || this.preparingToHide) {
			return;
		}

		// We are fixing to hide but may have to wait for or do a few things first
		this.preparingToHide = true;

		// When hiding is complete - check if we should remove this view
		const checkAndRemove = () => {
			// Remove? If it is not a simple el hide (caused by a flip and not any actual events) -
			// check if the view should be removed after hiding the instance
			if (
				(this._simpleHide !== true || this.removeOnSimpleHide) &&
				this.removeOnHide
			) {
				// Check that this view's viewCollection matches our original view collection
				// When popping views from one thing to another, we will always put the view back - there is no
				// permanant popping. It should be used to go into more focused/larger contexts but ultimately
				// still belongs to the viewCollection it was created in.
				// Also check that there are not any transient views - if there is a transient view then this
				// view that is hiding will be back when that view returns to its original home
				if (
					!this.viewSpec ||
					(this.viewSpec.currentCollection == null &&
						(!this.viewSpec.collection ||
							!this.viewSpec.collection.getTransient()))
				) {
					this.remove();
				}
			}
			// If not a simple hide, and it is not in original `viewCollection` then it needs to be removed
			// from the `currentCollection`
			else if (
				this._simpleHide !== true &&
				this.viewSpec &&
				this.viewSpec.currentCollection
			) {
				this.viewSpec.currentCollection.remove(this.viewSpec);
			}
			delete this._simpleHide;

			// It's over, we are hidden (and possibly removed)
			this.preparingToHide = false;
		};

		// In case hide was called while we were still in the middle of showing
		// Let show finish and then we can hide, this way nothing is jerky in and out of the app
		$.when(this.showing ? this.whenShown : true).then(() => {
			// To save people from themselves we always have a slight hide delay in most cases
			return $.when(this.hideDelayed ? this.hideDelayed : true).then(() => {
				this.hiddenByProxy = false;
				// Not already hiding
				// I do not think there is harm in hiding something already hidden - if there is we need to fine tune a few more things
				// as if you race fast enought you can be visible to the user while not being quite "visible" to the code and/or vice versa
				if (!this.hiding && this.isShown) {
					// We are now officially starting to hide
					this.trigger('hiding', this);

					// NOTE: We do not do any fliiping in here as it is assumed the event system is taking care of showing something else
					// It is in the `show` method that we help out with flipping

					// Wait for view to be considered hidden
					this.beforeHidden(() => {
						// We are now officially hidden
						this.trigger('hidden', this);
						// Remove?
						checkAndRemove();
					});
				}
				// We are already hidden
				else if (!this.isShown) {
					// Remove?
					checkAndRemove();
				}
			});
		});

		return this;
	}

	/**
	 * @method
	 * @return {array} Excluded views
	 */
	getExcludedViews() {
		return _.pluck(this.subViews, 'el').concat(_.pluck(this.menuViews, 'el'));
	}

	/**
	 * @method
	 * @param {string} functionalityName Functionality name
	 * @return {boolean}
	 */
	hasFunctionality(functionalityName) {
		return this.constructor.hasFunctionality(functionalityName);
	}

	/**
	 * Called by view.initialize() to parse the template for the attributes 'data-action' and
	 * 'data-action-context', which sets up listeners and does basic parsing.
	 * @method
	 * @private
	 */
	initializeActions() {
		// TODO: We need to support our multiView stuff in actions so we do not need to set up wrappers on every action related to
		// a modal crumb or tab item (these are created via child views and there models are the viewSpec - but all actions want the view related to the viewSpec)
		// var _view = view.model instanceof AppViewModel ? view.model.get('view') : view;
		// This needs to be handled when triggering a wrapper and enbleTest

		this.$actionEls = this.$('.nx-action').purge(this.getExcludedViews());
		if (this.el.classList.contains('nx-action')) {
			this.$actionEls.push(this.el);
		}
		// if this view has action context, parse it.
		// find all actions & menus in the view;
		// exclude those that actually belong to child views;
		// build arrays of their names
		this.actions = this.$actionEls.map(function () {
			return $(this).data('action');
		});
		this.menus = this.$('.nx-action-context')
			.purge(this.getExcludedViews())
			.map(function () {
				return $(this).data('action-context');
			});

		// find syncActions & userActions
		this.syncActions = [];
		this.specificSyncActions = [];
		this.userActions = [];

		this.menuActions = this.menuActions || {};

		// test all the actions we have buttons for
		_.each(this.actions, (c, idx, all) => {
			const a = FindLegacyAction(c);
			if (_.isUndefined(a)) {
				console.warn('The action "' + c + "' has not been registered.");
				// add a minimal fake action, so we still trigger an event
				RegisterLegacyAction({event: c, fake: true});
			} else {
				if (_.has(a, 'testOnSync')) {
					if (a.testOnSync !== true) {
						this.specificSyncActions.push(a);
					} else {
						this.syncActions.push(a);
					}
				}
				if (_.has(a, 'userLevel')) {
					this.userActions.push(a);
				}
			}
		});

		// test all the actions we have menus (menus) for.
		_.each(this.menus, (menuName) => {
			// search Netx.Menus instead (or also)
			if (Netx.Menus[menuName]) {
				// TODO: refactor this.menus, this.menuViews, this.menuActions, etc, into one property on this
				this.menuActions[menuName] = Netx.Menus[menuName].toActions();
			} else {
				// Thanks to this interesting code,
				// if we have an action named 'foo',
				// this lets us say 'data-nx-context="foo"' instead of 'data-nx-action="foo"' ....
				// But is this a feature we use?  Does it even work?  What does it do?  Bug maybe?  Deprecate?
				console.warn('using actions as menus is deprecated, gone in 8.13');
				this.menuActions[menuName] = Netx.Actions[menuName];
			}

			_.each(this.menuActions[menuName], (a) => {
				if (_.has(a, 'testOnSync')) {
					if (a.testOnSync !== true) {
						this.specificSyncActions.push(a);
					} else {
						this.syncActions.push(a);
					}
				}
				if (_.has(a, 'userLevel')) {
					this.userActions.push(a);
				}
			});
		});

		// Support appView instances - proxy through the actual view involved in that case
		// This is used mostly in cases where a appTabITemView is involved - the view itself does not have any syncable models/collections
		// But the view it works for does
		const view =
			this.model && this.model.isAppView ? this.model.get('view') : this;

		this.specificSyncActions.forEach((action) => {
			if (this.configureSpecificSyncActions) {
				this.stopListening(
					action.testOnSync,
					'sync reset',
					this.configureSpecificSyncActions,
				);
			}
			const syncListener = (this.configureSpecificSyncActions = () => {
				this.configureActions([action]);
			});
			this.listenTo(
				action.testOnSync,
				'sync reset',
				this.configureSpecificSyncActions,
			);
		});

		if (view) {
			// Configure sync listeners for sync actions.
			// We have sync actions
			if (this.syncActions.length) {
				// But we haven't set up listeners yet, so set them up now
				if (!this.syncActionsListening) {
					if (view.collection) {
						// not happening on collections?
						this.stopListening(
							view.collection,
							'sync reset',
							this.configureSyncActions,
						);
						this.listenTo(
							view.collection,
							'sync reset',
							this.configureSyncActions,
						);
					}
					if (view.model) {
						this.stopListening(view.model, 'sync', this.configureSyncActions);
						this.listenTo(view.model, 'sync', this.configureSyncActions);
					}
					// avoid re-adding these listeners.
					this.syncActionsListening = true;
				}
			}
		}

		// We have no sync actions.
		// But we have listeners (unlikely but not impossible), so remove them.
		else if (this.syncActionsListening) {
			this.stopListening(
				view.collection,
				'sync reset',
				this.configureSyncActions,
			);
			this.stopListening(view.model, 'sync', this.configureSyncActions);
			delete this.syncActionsListening;
		}

		// Configure sync listeners for user actions ...
		// TODO: this was from when we had to wait for Netx.user.profile.  Now that we are assured of having that object before
		// the application starts, I'm not sure this matters any more.
		if (this.userActions.length) {
			if (!this.userActionsListening) {
				this.stopListening(userProfile, 'sync', this.configureUserActions);
				this.listenTo(userProfile, 'sync', this.configureUserActions); // could be "change:userLevel" instead of "sync" ...
				this.userActionsListening = true;
			}
		} else {
			if (this.userActionsListening) {
				this.stopListening(userProfile, 'sync', this.configureUserActions);
			}
		}
	}

	/*
	 * initializeKeyListeners() is called by view.initialize() and checks for key events
	 * @private
	 */
	initializeKeyListeners() {
		// Listen for escape key if property is set
		if (!_.isFunction(this.escape)) return this;

		return this.stopListening(
			dispatcher,
			'key:escape',
			this.escapeKeyListener,
		).listenTo(dispatcher, 'key:escape', this.escapeKeyListener);
	}

	/**
	 * this.insertContextMenu() inserts a given menu in this view, positioned underneath the mouse pointer.
	 * @param string menuName
	 * @param {event} e The original click event (required for positioning)
	 */
	insertContextMenu(menuName, e) {
		const menuOptions = {
			className: 'dropdown',
			model: this.model,
			collection: this.menuCollection || this.collection,
			context: true,
		};

		if (!this.useAdvancedMenu) {
			menuOptions.menu = Netx.Menus[menuName];
			// Warn about obsolete menus:
			if (menuOptions.menu && menuOptions.menu.get('deprecated')) {
				console.warn(
					`Menu ${menuName} deprecated, gone after ${menuOptions.menu.get(
						'deprecated',
					)}.`,
				);
			}
		}

		// Look for custom template
		const templateName = this.useAdvancedMenu
			? menuName
			: (this.$el.attr('data-nx-template') || '').replace('menuView-', '');
		if (templateName != null) {
			menuOptions.template = MenuTemplates[templateName];
		}

		// Menu view type
		const menuView = (this.menuView = this.useAdvancedMenu
			? new Netx.Views.AdvancedMenuView(menuOptions)
			: new Netx.Views.MenuView(menuOptions));

		// Where to append menu
		let appendTo = this.$el.data('contextmenu-container') || 'body';

		// Set it apart
		menuView.$el.addClass('contextmenu');

		// Render and show
		menuView.render().$el.appendTo(appendTo);

		// iOS safari has some real issues
		// if we do not do something here the menu will either hide immediately
		// or an action that is now beneath your finger will fire
		let canHide = true;
		if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
			canHide = false;
			menuView.$el.find('.dropdown-menu').css('pointer-events', 'none');
			_.delay(() => {
				canHide = true;
				menuView.$el.find('.dropdown-menu').css('pointer-events', 'initial');
			}, 300);
		}

		// show menu
		menuView.$el.find('[data-toggle="dropdown"]').click();

		// Move the menu underneath the mouse
		menuView.$el.css(_.getMenuPosition(e, menuView.$el));

		// Remove and destroy the menuView when it gets closed.
		menuView.$el.on('hide.bs.dropdown', () => {
			if (!canHide) {
				// Reshow
				menuView.$el.find('[data-toggle="dropdown"]').click();
				return;
			}

			_.defer(() => {
				this.trigger('context:menu:removed', menuView);
				menuView.$el.off('hide.bs.dropdown');
				menuView.remove();
				delete this.menuView;
			});
		});

		// Make menuView remove itself when its parent (this) is removed
		menuView.stopListening(this, 'removed').listenTo(this, 'removed', () => {
			menuView.remove();
		});

		// deprecated 2016-07-01 - Left for legacy as no longer are we deferred thanks to pre-compiling our templates
		// TODO: actually go and remove the listeners for this event from the three views that use it.
		this.trigger('context:menu:rendered', menuView);
	}

	/**
	 * insertMenu creates a menuView and inserts it in the given element, which must have a data-action-context attr.
	 * @method
	 * @private
	 * @param {jQuery}  $menuEl  Selector for the data-action-context element
	 * @param {string}  menuName Name of the menu (key to this.menus)
	 * @param {boolean} vanish   Should this menu remove itself when it hides?
	 * @return {Netx.View} Menu view
	 */
	insertMenu($menuEl, menuName, vanish) {
		if (!menuName) {
			throw new Error('insertMenu called on non-menu');
		}

		// TODO: Talk about the fact model is undefined and collection has 0 models
		// But- really in the context of where I was testing- appHeader - was one hoping the menu would be privy to the view it was called in's model and collection? Maybe?
		var menuOptions = {
			model: this.model,
			collection: this.collection,
			menu: Netx.Menus[menuName],
		};

		if (!menuOptions.menu) {
			throw new Error('Can\'t find menu "' + menuName + '"');
		}

		// Warn about obsolete menus:
		if (menuOptions.menu.get('deprecated')) {
			console.warn(
				'Menu ' +
					menuName +
					' deprecated, gone after ' +
					menuOptions.menu.get('deprecated') +
					'.',
			);
		}

		// Look for custom template
		var templateName = ($menuEl.attr('data-nx-template') || '').replace(
			'menuView-',
			'',
		);
		if (templateName != null) {
			menuOptions.template = MenuTemplates[templateName];
		}

		// Create menu view if it doesn't exit
		this.menuViews = this.menuViews || {};

		// Find/create view, and attach.
		var mv = this.menuViews[menuName] || new Netx.Views.MenuView(menuOptions);
		this.menuViews[menuName] = mv;
		if (mv.el !== $menuEl && mv.$el !== $menuEl) {
			mv.setElement($menuEl);
		}

		var $parent = mv.$el.parent();

		// The menu can remove itself when hidden
		if (vanish) {
			// Remove and destroy the menuView when it gets closed.
			$parent.off('hide.bs.dropdown.nx').on(
				'hide.bs.dropdown.nx',
				_.bind(function (e) {
					// This isn't going to work
					// By stopping propagation, it does not just stop the flow of the event upwards, it stops any
					// other event listener on the element for this event - namespace is not a consideration.
					// Instead: just respond to the event if it pertains directly to us and then we do not need
					// to worry about the upward flow of the event
					// e.stopPropagation();
					if (e.target == $parent[0]) {
						// Delete the menu view ... this is more complicated than it should be.
						// This defer allows any other event listener to do its thing. Whithout it you risk flickers and other oddities.
						_.defer(
							_.bind(function () {
								$parent.off('hide.bs.dropdown.nx');
								// We can't just call mv.remove(), but we can't because we installed our view on a UL element and we need to leave that element behind.
								mv.removeInner();
								delete this.menuViews[menuName];
							}, this),
						);
					}
				}, this),
			);
		}

		// make mv remove itself when its parent (this) is removed
		mv.stopListening(this, 'removed');
		mv.listenTo(this, 'removed', function () {
			if (vanish) {
				// We create a lot of menus - be safe and overkill events
				$parent.off('hide.bs.dropdown.nx');
			}
			// because .listenTo sets context, this 'this' == mv.
			this.remove();
		});

		// Return menu view
		return mv;
	}

	/**
	 * A view can be attached to either a Model or a Collection;
	 * this.isCollectionView() is the canonical way to tell the difference.
	 * @method
	 * @return {boolean} Is collection view
	 */
	isCollectionView() {
		return this.model == null;
	}

	/**
	 * For more complicated responses to route changes, a modal view can override this.isMyRoute();
	 * It receives the current route, and returns true if the route is one it expects to
	 * be showing for, or false if it expects to close.  The default implementation respects
	 * this.stickyModal.
	 * @method
	 * @param {string} route The current route.
	 * @return {boolean}
	 * @see {@link Netx.View#isMyRoute|isMyRoute}
	 */
	isMyRoute(route) {
		if (this.stickyModal) return true;
		if (this.routeMatch) {
			return _.isString(this.routeMatch)
				? route === this.routeMatch
				: !_.isNull(route.match(this.routeMatch));
		}
		return false;
	}

	/**
	 * Hide is about to start
	 * At this point no animations or actual hiding [should] have taken place
	 * @method
	 * @listens Netx.View#hiding
	 */
	onHiding(view) {
		if (view !== this) return;

		// Definitely not showing
		this.showing = false;
		// Set the appropriate hiding stuff
		this.hiding = true;
		this.el.removeAttribute('data-nx-showing');
		this.el.setAttribute('data-nx-hiding', true);
		//this.$el.removeClass('showing').addClass('hiding');
	}

	/**
	 * Show is about to start
	 * At this point no animation or actual showing [should] have taken place
	 * @method
	 * @listens Netx.View#showing
	 */
	onShowing(view) {
		if (view !== this) return;

		// Definitely not hiding anymore
		this.hiding = false;
		// Set the appropriate showing stuff
		this.showing = true;
		this.el.removeAttribute('data-nx-hiding');
		this.el.setAttribute('data-nx-showing', true);
		//this.$el.removeClass('hiding').addClass('showing');
	}

	/**
	 * Parse the contents of an action object and execute its action
	 *
	 * @param Object action An action object
	 * @param Array actionArgs A set of action arguments.
	 * @method
	 * @private
	 */
	performAction(action, actionArgs) {
		var e = _.last(actionArgs);

		//
		// Generic handler for all Action clicks:
		//
		if (action.fake) {
			console.warn(
				'The action "' + action.event + '" has not been registered.',
			);
		}

		// if this view has defined an actionWrapper for this action, run the arguments through that.
		if (
			this.actionWrappers &&
			_.isFunction(this.actionWrappers[action.event])
		) {
			actionArgs = this.actionWrappers[action.event].apply(this, actionArgs);
			if (!_.isArray(actionArgs)) {
				throw new Error(
					"view.actionWrapper['" + action.event + "'] did not return an array",
				);
			}
		}

		// or else, if the action itself defined a wrapper, run the arguments through that.
		else if (_.isFunction(action.wrapper)) {
			actionArgs = action.wrapper.apply(this, actionArgs);
			if (!_.isArray(actionArgs)) {
				throw new Error(
					'Action wrapper for ' + action.event + ' did not return an array',
				);
			}
		}

		// close any/all open dropdown menus
		// TODO: have the menus respond to an event, instead of scraping the entire DOM on every click.
		dispatcher.trigger('app:close:menus');
		//$('.dropdown.open .dropdown-toggle').click();

		// If this action has a route, route to it
		if (_.isFunction(action.route)) {
			// route can be a function that takes args & returns a string
			appRouter.navigate(action.route.apply(this, actionArgs), true);
		} else if (_.isString(action.route)) {
			// it can also be a plain string.
			// TODO: such actions could be rendered as simple hrefs that can be enabled/disabled, so the browser can do this part ...
			appRouter.navigate(action.route, true);
		}

		// If this action has a link, send the browser to it.
		// (Again, this could have been rendered as a simple href ...)
		else if (_.isString(action.link)) {
			// a link could be of the format "#route", or "/other/site/on/this/host" or even "http://google.com"
			window.location = action.link;
			return;
		}

		// If no view & no route, trigger this action's event
		else {
			actionArgs.unshift(action.event);
			dispatcher.trigger.apply(dispatcher, actionArgs);
		}

		this.trigger('performedAction', action);
	}

	/**
	 * Prevent default event
	 * @method
	 */
	preventDefault(e) {
		e && e.preventDefault && e.preventDefault();
		return false;
	}

	/**
	 * Pre-Render hook: preRender() is called by render(), and runs first.
	 * The default method is null by default, and can be overridden without concern.
	 * It will not be called until the template is loaded.
	 * @see {@link Netx.View#postRender|postRender}
	 * @see {@link Netx.View#render|render}
	 */
	preRender() {
		// Cleanup lazyloader
		this.Plugins.Destroy('lazyloader');

		return this;
	}

	/**
	 * Post-Render hook: postRender() is called immediately after render completes.
	 * The default method is null by default and can be overriden without concern.
	 * It will not be called until the template is loaded.
	 * @see {@link Netx.View#preRender|preRender}
	 * @see {@link Netx.View#render|render}
	 */
	postRender() {
		//console.debug('POST-RENDER');
	}

	/** @override */
	remove() {
		// This is already happening
		if (this.removing) return;

		this.trigger('removeHook', this);

		this.cleanUpActionEvents();
		delete this.$actionEls;

		// We want to know when we are removing
		// The reason being - when we remove we fire off all appropriate events to hide/remove/cancel our children - the thing is - when children remove
		// we check to see if there is anything left in our viewCollection - if nothing is left we fire `this.hide` - essentially starting this whole thing over -
		// which confuses appModalView as the view is already gone as far as it was concerned but the hide event gets back to it - it check its viewCollection
		// length - see's only 1 and assumes that that is it - modal game over (as why bother trying to do anything fancy if only one view is there at hide time)
		this.removing = true;

		this.menuView && this.menuView.remove();

		this.unbindEvents();

		// clean up any pending stuff
		this._hiddenDeferred.state() === 'pending' &&
			this._hiddenDeferred.resolve();
		this._shownDeferred.state() === 'pending' && this._shownDeferred.resolve();
		this.rendered.state() === 'pending' && this.rendered.reject();
		this.whenImagesLoaded &&
			this.whenImagesLoaded.state() === 'pending' &&
			this.whenImagesLoaded.reject();
		this.whenShown.state() === 'pending' && this.whenShown.reject();
		this.whenHidden.state() === 'pending' && this.whenHidden.reject();

		// Destroy all plugins
		this.Plugins.Destroy();

		// Remove any children we may have.
		this.removeAllSubViews();

		this.pseudoProgressInterval && clearInterval(this.pseudoProgressInterval);

		super.remove();

		Netx.viewStats[this.constructor.__name__]--;

		this.trigger('removed');

		this.off();
	}

	/**
	 * @method
	 */
	removeAllSubViews() {
		_.invoke(this.subViews, 'remove');
		this.subViews = {};
		return this;
	}

	/**
	 * If a child is removing a view added with addSubView(), they should call removeSubView() to clean up its reference
	 * so memory can be freed.
	 * @method
	 * @param {string|Netx.View} idx Either a view or a view's .cid (which is a unique ID)
	 * @param {boolean} destroyView Whether or not to destroy the view
	 * @see {Netx.View#addSubView}
	 */
	removeSubView(idx, destroyView) {
		if (idx === void 0) return;

		if (idx.cid && idx.cid.includes('view')) {
			idx = idx.cid;
		}

		const view = this.subViews[idx];
		delete this.subViews[idx];
		destroyView && view && view.remove();
		return view;
	}

	/**
	 * Render: paint a view's DOM objects.
	 * render() calls doTemplate() to generate templated HTML, and installs that HTML in the
	 * view's element, $el.  The preRender() and postRender() hooks are called before and after.
	 *
	 * NOTE: render() is asynchronous.  Templates load on demand as views are instantiated, and if
	 * render() is called before the template is loaded it returns immediately, but actual
	 * rendering is deferred until the template is available.  Listen for the Backbone event
	 * "rendered" on the View object itself (not the dispatcher) to know when rendering has completed,
	 * or attach a done handler to the Promise object this.rendered .
	 *
	 * render() is quite complex; ideally, views will not need to override this method,
	 * and will override the pre- and post- hooks instead.  If you must override render,
	 * be sure to call it in your custom render method via this._super().
	 *
	 * render() returns the view itself, for daisychaining in the Backbone pattern.
	 *
	 * @see {@link Netx.View#doTemplate|doTemplate}
	 * @see {@link Netx.View#preRender|preRender}
	 * @see {@link Netx.View#postRender|postRender}
	 * @see {@link Netx.View#renderElement|renderElement}
	 */
	//
	// DEVNOTE: the definition below of render() is wrapped in the initializer (above), which
	// passes it the argument 'deferred'.  But for the sake of clarity I'm putting the
	// documentation here for the external render() method (i.e. the wrapper), which does not
	// take an argument.
	//
	// ... and yes, we need to do it that way, in order to implement pre- and post- hooks on any overridden render() method.
	//
	render() {
		// We debounce some renders - do not render if we are removing/removed
		if (this.removing) return;

		// This is a cleaner way of having to check - `!this.renderedOnce` then `this.listenToOnce( this, 'rendered', ... )`
		// You can just say `this.rendered.then(...)`
		// Usefule for things that get declared in the initialize method but do not want to act until the view has first rendered
		if (this.rendered.state() !== 'pending') {
			this.rendered = new $.Deferred();
		}

		if (this.whenImagesLoaded && this.whenImagesLoaded.state() === 'pending') {
			this.whenImagesLoaded.reject();
		}
		// Let our view know when its images have loaded
		this.whenImagesLoaded = new $.Deferred();

		this.cleanUpActionEvents();

		// Call pre-render hook before we touch anything.
		this.trigger('preRenderHook', this);
		this.preRender();
		this.unbindEvents();

		// Modifying this.el begins here.
		// When adding anything new to the render process,
		// alway add it after this point, or else
		// preRender() can't access the unmodified version
		super.render();

		let html;
		// try to apply the template; catch any template errors.
		try {
			html = this.doTemplate();
		} catch (error) {
			console.error(`Exception in template ${this.constructor.__name__}`);
			throw error;
		}

		// Further research finds that this method is about 30% faster (on Chrome at least):
		if (!this.el) {
			// WTF? Caused by some extension code in custom controller, altering UserPrefsView and SearchRulesView by deleting 'el' in the prototypes.
			// TODO: move those extensions to the new extension system & this won't happen.
			// TODO: our views should not define 'el' in the first place.  Probably an old mistake.  Fix those in core, then delete this whole hack.
			// Meanwhile, revert to the old algorithm for just these two views.
			this.el = document.createElement(this.tagName);
			this.$el = $(this.el);
		}
		this.el.innerHTML = html;
		// TODO: if we could translate the html before adding it to the DOM, it might be faster still
		this.$el.localize();

		// attach model ID to all model views:
		if (this.model && this.model.id) {
			this.$el.attr('data-nx-model-id', this.model.id);
		}

		// process any AutoTemplate directives in the template (and potentially load other templates)
		dispatcher.trigger('autoTemplate:scan', this.$el);

		// install & configure Actions in this template
		this.initializeActions();
		this.renderActions();

		// Count/total collection items
		this.$count = this.$('.nx-count');
		// Do not interfere with children
		if (this.$childTargetEl && this.$childTargetEl.find(this.$count).length) {
			this.$count = null;
		}
		this.updateCount();

		// Done modifying this.el.
		// Don't add modifications after here, or
		// postRender() won't have access to them.

		// call post-render hook
		this.trigger('postRenderHook', this);
		this.postRender();

		// Add/remove `no-assets`/`no-results`/`no-matches` classes
		// TODO: Stop using `no-assets`, instead use `no-results`
		this.checkHasResults();

		// install key listeners
		this.initializeKeyListeners();

		// Re-attach event listeners.
		// http://ianstormtaylor.com/rendering-views-in-backbonejs-isnt-always-simple/
		this.delegateEvents();

		// Backstory:
		// We have relied on this rendered promise to so something once in initialize after it is resolved
		// In a lot of cases we only want this to happen on the first render - so moving the code to postRender is not going to be good for us
		// I do not see any harm in triggering a rendered event on the view that we can `listenToOnce` and accomplish what we want
		// I have deferred this event because we chanin our renders a lot - I do not want the event to fire until we are done with our chain
		_.defer(() => {
			// When we need to do something on only the first render, in some more complciated views, we can just
			// use `.then` rather than having to check `this.renderedOnce` -> `this.listenToOnce(this, 'rendered' ... )`
			// Use cases are for when you only want to do something once on rendered (so postRender will not cut it, though,
			// you could check for `this.renderedOnce` in `postRender`, but all the places we would need to do this, `postRender`
			// just doesn't seem like the appropriate place - `initialize` is a better place for 99% of that logic.
			this.rendered.resolveWith(this);
			this.trigger('rendered');
			// Now listen for view's images to load
			this.$el.imagesLoaded(() => {
				this.whenImagesLoaded.resolveWith(this);
			});

			this.bindEvents();
		});

		// We have rendered once (at least)
		this.renderedOnce = true;

		this.trigger('renderedHook', this);

		return this;
	}

	/**
	 * renderActions() is called after view.render(), to install context menus and buttons in the DOM.
	 * @method
	 * @private
	 */
	renderActions() {
		// are there any actions to activate?
		this.configureActions(_.map(this.actions, FindLegacyAction));

		// any menus to install?
		if (!this.menus.length) return this;

		_.each(this.menus, (menuName) => {
			var $menuEl = this.$(
				'.nx-action-context[data-action-context="' + menuName + '"]',
			);
			var triggerEvent =
				$menuEl.closest('[data-nx-trigger]').data('nxTrigger') || 'click';

			// TODO: this is a hack for DAM-12956 until we reqork menus. Not injecting and testing now could mean an empty menu
			// displaying until hover (at which point it disappears like it should have)
			if (
				triggerEvent !== 'mouseover' &&
				$menuEl.filter('.dropdown-menu').length > 0
			) {
				// we don't really "render" dropdowns any more; we wait for a triggerEvent, and create the menuView then.
				var $trigger = $menuEl.siblings('.dropdown-toggle'); // changed a.dropdown-toggle to .dropdown-toggle (buttons are dropdowns too!)
				$trigger.off(triggerEvent + '.data-action-context');
				$trigger.on(
					triggerEvent + '.data-action-context',
					_.bind(function () {
						this.insertMenu($menuEl, menuName, true).render();
						this.configureMenu(menuName);
					}, this),
				);
			} else {
				// normal menus are created now
				this.insertMenu($menuEl, menuName, false).render();
				this.configureMenu(menuName);
			}
		});

		return this;
	}

	/**
	 * Backbone generally updates the screen by re-rendering a view via view.render().  But sometimes we don't want to re-render
	 * a whole view; for instance, third-party plugins may be bothered if our DOM objects are deleted and re-created.
	 * renderElement() replaces just one element of our template, specified by a jQuery selector, with freshly template-generated HTML.
	 * Canonical usage:
	 * @example
	 * this.renderElement(selector, this.doTemplate());
	 * @see {@link Netx.View#render|render}
	 */
	renderElement(selector, the_html) {
		// convert the_html to DOM object.
		const jq = $('<div>' + the_html + '</div>');
		const the_el = jq.find(selector);
		if (the_el.length == 0) {
			console.warn('selector found nothing');
			return;
		}
		const new_html = the_el.html();
		// install where needed.
		this.$(selector).html(new_html).localize();
		// If there are actions in the new html, initialize and render them.
		if (
			new_html.match(/data-action=/) ||
			new_html.match(/data-action-context=/)
		) {
			this.initializeActions();
			this.renderActions();
		}
		// If there are autocomplete fields in the new html, initialize them.
		//this.detectAutocomplete(this.$(selector));
	}

	/**
	 * @method
	 * @param {event} e `search` event
	 */
	search(e) {
		let str = (_.isString(e) ? e : (e && e.detail) || '').trim();
		if (str && str.length >= 2) {
			this.collection.setFilterStr(str);
		}
		// Only clear if we previously had a search str of 2 characters or more
		else if (
			this.collection.filterStr &&
			this.collection.filterStr.length >= 2
		) {
			this.collection.setFilterStr('');
		}
	}

	/** @method */
	setCollection(collection, opts = {}) {
		if (this.collection && this.collection === collection) return;

		this.unbindCollectionEvents();

		this.collection = collection;
		_.invoke(this.menuViews, 'setCollection', collection);

		this.bindCollectionEvents();
		this.addLoadingClasses_internal();
		this.checkHasResults();

		if (this.renderOnSetCollection !== false && !opts.silent) {
			this.render();
		}
	}

	/**
	 * We want to maintain all class names when we set element
	 * @override
	 */
	setElement(element, delegate) {
		let className = this.el && this.el.classList ? this.el.className : [];
		className = `${className} ${this.className || ''}`;

		super.setElement(element, delegate);

		this.$el.addClass(className);

		return this;
	}

	/**
	 * Netx.View listens for 'setModel' events that pass a new model; these are routed to
	 * a .setModel(model, options) function.  Children of this class can add handling
	 * for model changes by overriding that method.  Ditto for 'setCollection'
	 */
	setModel(model, opts = {}) {
		if (this.model && this.model === model) return;

		this.unbindModelEvents();

		this.model = model;
		_.invoke(this.menuViews, 'setModel', model);

		this.bindModelEvents();
		this.addLoadingClasses_internal();

		if (this.renderOnSetModel !== false && !opts.silent) {
			this.render();
		}
	}

	/**
	 * Show the view; hide sibling views within the same viewCollection
	 * @method
	 * @fires Netx.View#showing
	 * @fires Netx.View#shown
	 * @see {@link Netx.View#hide|hide}
	 * @see {@link Netx.View#toggle|toggle}
	 */
	show() {
		// Do nothing if we are in the midst of being removed
		if (this.removing) return this;

		// We are no longer planning on hiding (or at least remaining hidden if a hide is in progress)
		this.preparingToHide = false;

		// In case hide was called while we were still in the middle of hiding
		// Let hide finish and then we can show, this way nothing is jerky in and out of the app
		// Also, make sure we are not removing when hide is over
		$.when(
			this.hiding && (this._simpleHide || !this.removeOnHide)
				? this.whenHidden
				: true,
		)
			.then(() => {
				this.shownByProxy = false;
				let hidePromise;
				// Hide any other visible siblings
				// Do this before showing the newly active view so we do not end up with uneven animations in/out
				// This will be firing off at almost the same time as `this.beforeShown`
				// This should happen regardless of if the view was already shown
				if (
					this.viewSpec &&
					(this.viewSpec.currentCollection || this.viewSpec.collection)
				) {
					// We want to toglle views in the right `viewCollection` context
					(this.viewSpec.currentCollection || this.viewSpec.collection).each(
						(viewSpec) => {
							// Sometimes - due to timing - we get into a position where we are looping
							// while a view is being removed (like a removeOnHide situation) - so just check
							// that the model is still defined
							if (viewSpec != null) {
								const _view = viewSpec.get('view');
								if (
									_view &&
									_view.cid !== this.cid &&
									(viewSpec.get('active') || _view.isShown)
								) {
									// Be sure to pass true as this was not caused by any actual events
									_view.hide(true);
									hidePromise = _view.whenHidden;
								}
							}
						},
					);
				}
				// Not already showing and is not shown
				if (!this.showing && !this.isShown) {
					return $.when(hidePromise || true).then(() => {
						// We are now officially starting to show
						this.trigger('showing', this);
						// Wait for view to be considered shown
						this.beforeShown(() => {
							// We are now officially shown
							this.trigger('shown', this);
						});
					});
				}
			})
			.catch((ex) => {
				console.error(`Error showing view`, this, '\n', ex);
			});
		return this;
	}

	/**
	 * showLoaded expects the same event signature as a Backbone.Sync event
	 * @method
	 * @param {Netx.Collection|Netx.Model} thing Instance of Netx.Collection or Netx.Model
	 * @param {mixed} resp Response
	 * @param {object} opts Request options
	 */
	showLoaded(thing, resp, opts = {}) {
		// PSEUDO PROGRESS
		this.endProgressSimulation();

		// If this is part of an app view collection - let the view model know
		// All loading events will bubble up to the app view collection and the main view can make decisions on how to handle it
		if (
			this.viewSpec != null &&
			thing != null &&
			!(thing instanceof this.constructor)
		) {
			this.viewSpec.trigger('loaded', thing, this);
		}

		const el = this.el;

		// Whether we use loading classes or not - we ought to remove this - because we do not check
		// for loading classes when we call this.showNotLoaded()
		el.removeAttribute('data-nx-not-loaded');

		this.checkHasResults();

		// Just because loading is done on the model/collection doesn't mean we have had enough time to deal with the render
		// You wind up seeing old data for a split second in a lot of places where we utilize .loading to hide information
		if (opts.loadingClasses !== false) {
			el.removeAttribute('data-nx-loading');
			el.classList.remove('loading-chunk');
		}

		this._loadedOnce = true;
	}

	/**
	 * showLoading expects the same event signature as a Backbone.Request event
	 * @method
	 * @param {Netx.Collection|Netx.Model} thing Instance of Netx.Collection or Netx.Model
	 * @param {object} xhr XHR object
	 * @param {object} opts Request options
	 */
	showLoading(thing, xhr, opts = {}) {
		if (this.showLoadingOnce && this._loadedOnce) {
			return;
		}

		// PSEUDO PROGRESS
		this.simulateProgress();

		const el = this.el;

		el.removeAttribute('data-nx-no-assets');
		el.removeAttribute('data-nx-no-results');

		// TODO: convert over to the pseudo progress?
		// Just in case - we also monitor this cleanup in remove - do not want a rogue interval out there
		this.listenToOnce(dispatcher, 'cart:removed', (assetIds, cartId) => {
			if (this.interval) clearInterval(this.interval);
		});

		// If this is part of an app view collection - let the view model know
		// All loading events will bubble up to the app view collection and the main view can make decisions on how to handle it
		// The only time `thing` is a view is when this is called from the `addedChildView` listener in `addLoadingClasses_internal` method
		if (
			this.viewSpec != null &&
			thing != null &&
			!(thing instanceof this.constructor)
		) {
			this.viewSpec.trigger('loading', thing, this);
		}

		// Whether we use loading classes or not - we ought to remove this - because we do not check
		// for loading classes when we call this.showNotLoaded() - because mainly, we do not have those options
		// at that time
		el.removeAttribute('data-nx-not-loaded');

		// Cant test thing here because of inconsistencies in thing
		if (opts.loadingClasses !== false) {
			(this.collection && this.collection.loadingChunk) ||
			(thing && thing.loadingChunk)
				? el.classList.add('loading-chunk')
				: el.setAttribute('data-nx-loading', true);
		}

		return this;
	}

	/**
	 * Adds `not-loaded` attribute to view's el
	 * @method
	 */
	showNotLoaded() {
		const el = this.el;
		if (!el) return;

		el.setAttribute('data-nx-not-loaded', true);
	}

	/**
	 * Simulates progress on this.$pseudoProgressBar until this.endProgressSimulation is called
	 * @method
	 */
	simulateProgress() {
		if (
			this.simulatingProgress ||
			!this.$pseudoProgressBar ||
			!this.$pseudoProgressBar.length
		)
			return;

		// Simulation start
		this.simulatingProgress = true;

		const min = Math.min;
		const $pseudoProgressBar = this.$pseudoProgressBar;
		let pseudoProgressWidth = 0;

		$pseudoProgressBar.width('0%').attr('aria-valuenow', 0);

		this.el.setAttribute('data-nx-simulating-progress', true);
		this.$pseudoProgress.addClass('active');

		const draw = () => {
			pseudoProgressWidth = min(
				pseudoProgressWidth + (100 - pseudoProgressWidth) * 0.025,
				100,
			);

			$pseudoProgressBar
				.width(pseudoProgressWidth + '%')
				.attr('aria-valuenow', pseudoProgressWidth);

			this.simulatingProgress && window.requestAnimationFrame(draw);
		};
		draw();
	}

	/**
	 * Stop event propagation
	 * @method
	 */
	stopPropagation(e) {
		e && e.stopPropagation();
	}

	/**
	 * this.toggle() calls show() if currently hidden, or hide() if currently shown.
	 * @see {@link Netx.View#show|show}
	 * @see {@link Netx.View#hide|hide}
	 * @fires 'shown' on View object
	 */
	toggle() {
		return this.isShown ? this.hide(true) : this.show();
	}

	/**
	 * @method
	 */
	unbindCollectionEvents() {
		const collection = this.listenToCollection || this.collection;
		if (!collection) return this;

		this.trigger('unbindCollectionEventsHook', this);

		return this.stopListening(collection);
	}

	/**
	 * @method
	 */
	unbindEvents() {
		this.hammer && this.hammer.destroy();
		delete this.hammer;

		return this;
	}

	/**
	 * @method
	 */
	unbindModelEvents() {
		if (!this.model) return this;

		this.trigger('unbindModelEventsHook', this);

		return this.stopListening(this.model);
	}

	/**
	 * @method
	 */
	updateCount(count) {
		// Might not have rendered yet
		if (!this.$count) return;

		if (count == null) {
			const collection = this.listenToCollection || this.collection;
			if (
				!collection ||
				!this.$count ||
				!this.$count.length ||
				(this.requireSync && !collection.syncedOnce)
			)
				return;

			count = collection.getTotal();
		}

		this.$count.attr('data-count', count);

		this.i18nCountStr
			? this.$count.html(
					i18n.t(this.i18nCountStr, {
						count: count,
						defaultValue: '__count__ items',
					}),
			  )
			: this.$count.html(count);

		this.$count.attr(
			'aria-label',
			i18n.t('i18n.nItems', {
				count: count,
			}),
		);
	}

	/**
	 * Add functions to execute at instance initialization
	 * @method
	 * @param {function} cb Callback
	 * @param {string} functionalityName Functionality name
	 */
	static addInitializer(cb, functionalityName) {
		// We must recreate these as a parent class may have already had functionality extended to it,
		// which means it would have already had an array on the class
		// The next view that adds functionality would push it into the parent class' array
		// polluting all children that extended that parent class with another extended class' functionality
		const proto = Object.getPrototypeOf(this.prototype);

		// Super already has some functionality
		if (proto.constructor.__functionality__) {
			this.__functionality__ = _.unique(
				proto.constructor.__functionality__.concat(
					this.__functionality__ || [],
				),
			).concat([functionalityName]);
		} else {
			this.__functionality__ = (this.__functionality__ || []).concat([
				functionalityName,
			]);
		}

		// Super already has some functionality
		if (proto.constructor.__initializers__) {
			this.__initializers__ = _.unique(
				proto.constructor.__initializers__.concat(this.__initializers__ || []),
			).concat([cb]);
		} else {
			this.__initializers__ = (this.__initializers__ || []).concat([cb]);
		}
	}

	/**
	 * Extend functionality on the view's super
	 * @method
	 * @param {object} functionality Functionality to extend
	 */
	static extendFunctionality(functionality) {
		const proto = Object.getPrototypeOf(this.prototype);
		const myProto = Object.getPrototypeOf(this);
		for (let prop in functionality) {
			if (functionality.hasOwnProperty(prop)) {
				if (!proto[prop]) {
					proto[prop] = functionality[prop];
				} else {
					myProto[prop] = functionality[prop];
				}
			}
		}
		return proto;
	}

	/**
	 * Extend functionality on the view's super
	 * @method
	 * @param {string} functionalityName Functionality name
	 * @return {boolean}
	 */
	static hasFunctionality(functionalityName) {
		const functionality = this.__functionality__ || [];
		return functionality.includes(functionalityName);
	}

	/**
	 * Wrap a method on base view (functionality should not know anything about views extending base)
	 * @method
	 * @param {string} methodName Name of method to wrap
	 * @param {function} cb Callback
	 */
	static wrapMethod(methodName, cb) {
		const proto = Object.getPrototypeOf(this.prototype);
		if (!proto[methodName]) return proto;
		proto[methodName] = _.wrap(proto[methodName], cb);
		return proto;
	}

	/** @type {boolean} */
	static get __isView__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'View';
	}
}

export default View;
